const OAUTH_ERRORS = {
  denied: "Strava connection was cancelled. You can connect again.",
  exchange_failed: "Strava could not be connected. Try again.",
  scope_insufficient: "Strava did not allow activity access. Connect again and allow it.",
  athlete_conflict: "That Strava account is already linked to another Endurava account.",
};

export function athleteStravaView(statusData) {
  if (!statusData) {
    return { state: "LOADING", label: "", message: null };
  }
  const status = statusData.status || (statusData.connected ? "connected" : "disconnected");
  if (status === "deauthorized" || status === "reconnect_required") {
    return {
      state: "RECONNECT",
      label: "Reconnect",
      message: "Strava connection expired. Reconnect Strava to import again.",
    };
  }
  if (status === "disabled" || status === "disconnected" || statusData.connected === false) {
    return { state: "NOT_CONNECTED", label: "Not connected", message: null };
  }
  if (status === "failed") {
    return {
      state: "ERROR",
      label: "Try again",
      message: "Strava could not be reached. Try Sync again.",
    };
  }
  const history = statusData.historical_sync_status;
  if (status === "rate_limited" || history === "rate_limited" || history === "paused") {
    return { state: "PAUSED", label: "Sync paused", message: null };
  }
  const importing = status === "syncing"
    || status === "partially_synced"
    || history === "running"
    || statusData.historical_sync_exhausted === false
    || statusData.auto_sync_active === true;
  if (importing) {
    return { state: "IMPORTING", label: "Importing", message: null };
  }
  if (statusData.connected) {
    return { state: "CONNECTED", label: "Connected", message: null };
  }
  return { state: "NOT_CONNECTED", label: "Not connected", message: null };
}

export function formatStravaHistory(statusData) {
  const view = athleteStravaView(statusData);
  if (view.state === "NOT_CONNECTED" || view.state === "RECONNECT" || view.state === "LOADING" || view.state === "ERROR") {
    return null;
  }
  const imported = statusData?.imported_activity_count;
  const count = Number.isFinite(imported) ? imported : null;
  if (view.state === "PAUSED") {
    return "Sync paused — will resume automatically";
  }
  if (statusData?.historical_sync_exhausted === true) {
    return count == null ? "History synced" : `History synced. ${count} activities imported.`;
  }
  if (view.state === "IMPORTING") {
    return count == null
      ? "Importing activity history…"
      : `Importing activity history… ${count} activities imported`;
  }
  return null;
}

export function formatStravaImportProgress(statusData) {
  return formatStravaHistory(statusData);
}

export function formatStravaLastSync(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${hours}:${minutes} UTC`;
}

export function stravaOauthErrorMessage(code) {
  if (!code) return "Strava could not be connected. Try again.";
  return OAUTH_ERRORS[code] || "Strava could not be connected. Try again.";
}

export function isStravaAutoSyncActive(statusData) {
  return Boolean(statusData?.connected && statusData?.auto_sync_active);
}

export function stravaStatusLabel(status) {
  return athleteStravaView({
    status,
    connected: status !== "disconnected" && status !== "deauthorized" && status !== "disabled",
  }).label;
}

export function stravaStatusHint(status) {
  if (status === "rate_limited") return "Sync paused — will resume automatically";
  return athleteStravaView({
    status,
    connected: status !== "disconnected" && status !== "deauthorized" && status !== "disabled",
  }).message;
}

export function isStravaConnected(statusData) {
  return Boolean(statusData?.connected) && !needsStravaReconnect(statusData);
}

export function needsStravaReconnect(statusData) {
  const status = statusData?.status;
  return status === "deauthorized" || status === "reconnect_required";
}

export function canStravaSync(statusData, { syncing = false } = {}) {
  if (syncing) return false;
  if (!isStravaConnected(statusData)) return false;
  const status = statusData?.status;
  if (status === "syncing" || status === "rate_limited" || status === "partially_synced") return false;
  if (statusData.historical_sync_status === "paused" || statusData.historical_sync_status === "rate_limited") {
    return false;
  }
  return true;
}

export function shouldShowStravaConnect(configured, statusData, userConnected) {
  if (!configured) return false;
  if (needsStravaReconnect(statusData)) return true;
  if (statusData && statusData.connected === false) return true;
  if (statusData && isStravaConnected(statusData)) return false;
  return !userConnected;
}
